import { useEffect, useRef, useState } from 'react'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SignatureDialog } from '@/components/signature/SignatureDialog'
import { apiErrorMessage } from '@/lib/apiErrorMessage'

/** Mirrors `@MaxLength` on the server's CancelTransferDto. */
const REASON_MAX_LENGTH = 1000

/**
 * Cancel a transfer plan: the shared Part 11 signature plus a required reason,
 * both recorded on the plan's audit trail.
 *
 * Signed because cancelling is what unfreezes the source — the unit starts
 * taking stock again as if the plan never existed, so the trail has to prove
 * who lifted the freeze. Closing the dialog sends nothing.
 *
 * Same shape as `UnlockFormDialog`, on purpose: one signed-act-with-reason
 * pattern across the app.
 */
export function CancelTransferDialog({
  open,
  onOpenChange,
  sourceCode,
  signerName,
  onCancel,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The unit being unfrozen, named in the warning. */
  sourceCode: string
  signerName?: string
  /** Performs the signed cancel; rejects with the server's refusal. */
  onCancel: (reason: string, signingToken: string) => Promise<unknown>
}) {
  const [reason, setReason] = useState('')
  // Read at send time, not from the render that started signing: the password
  // check is async, and text typed meanwhile must not be dropped.
  const reasonRef = useRef('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Each cancel is its own deliberate act: never prefill the last reason.
  useEffect(() => {
    if (open) {
      setReason('')
      reasonRef.current = ''
      setError(null)
    }
  }, [open])

  const cancel = async (signingToken: string) => {
    setBusy(true)
    setError(null)
    try {
      await onCancel(reasonRef.current.trim(), signingToken)
      onOpenChange(false)
    } catch (err) {
      setError(apiErrorMessage(err, 'The plan could not be cancelled.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SignatureDialog
      open={open}
      onOpenChange={onOpenChange}
      title='Cancel this transfer plan'
      description={`Cancelling lifts the hold on ${sourceCode}: it takes stock again as if this plan never existed. This cannot be undone.`}
      confirmLabel='Sign & Cancel plan'
      signerName={signerName}
      confirmDisabled={reason.trim() === ''}
      busy={busy}
      error={error}
      onConfirm={(token) => void cancel(token)}
    >
      <div className='space-y-1.5'>
        <Label htmlFor='cancel-transfer-reason'>
          Reason <span className='text-destructive'>*</span>
        </Label>
        <Textarea
          id='cancel-transfer-reason'
          rows={3}
          maxLength={REASON_MAX_LENGTH}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value)
            reasonRef.current = e.target.value
          }}
          placeholder='Why is this plan being abandoned?'
          autoFocus
        />
      </div>
    </SignatureDialog>
  )
}
